import { useContext, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { GlobalContext } from '../../../../library/Core/GlobalContext';
import { BreakupSectionData } from '../types';
import {
    BreakupFilterKind,
    buildAlumniDirectoryPath,
    companyDisplayName,
    resolveCompanyId,
    resolveRowCode,
} from '../directoryLinks';
import { DONUT_PALETTE, formatNum, formatPct } from '../chartSetup';
import { breakupNameLink, companiesTooltip, companyChip, companyChipLink, pctPill } from './styles';
import SectionCard from './SectionCard';
import SectionStateCard from './SectionStateCard';

// Industry Wise Breakup / Functional Area Wise Breakup share this layout:
// a donut on the left, and a chip-list table (name + top companies | # | %) on the right.
interface BreakupSectionProps {
    title: string;
    nameHeader: string; // column header, e.g. "Industry"
    unit: string; // subtitle noun, e.g. "industries"
    data: BreakupSectionData | null;
    error?: string;
    emptyMessage?: string;
    filterKind?: BreakupFilterKind;
    campusId?: string | null;
    enableDirectoryLinks?: boolean;
}

const BreakupSection = ({
    title,
    nameHeader,
    unit,
    data,
    error,
    emptyMessage,
    filterKind,
    campusId,
    enableDirectoryLinks = false,
}: BreakupSectionProps) => {
    // Index of the row whose company list is currently revealed.
    const [hoveredRow, setHoveredRow] = useState<number | null>(null);
    const { globalState } = useContext(GlobalContext);
    const domain = globalState?.domain;

    if (error) {
        return (
            <SectionCard title={title}>
                <SectionStateCard variant="error" message={error} />
            </SectionCard>
        );
    }
    if (!data || data.rows.length === 0) {
        return (
            <SectionCard title={title}>
                <SectionStateCard variant="empty" message={emptyMessage} />
            </SectionCard>
        );
    }

    const rows = data.rows;
    // Reads "…for this industry" / "…for this functional area" depending on the section.
    const companiesLabel = `Top companies for this ${nameHeader.toLowerCase()}`;
    const chartData = {
        labels: rows.map((r) => r.name),
        datasets: [
            {
                data: rows.map((r) => r.count),
                backgroundColor: rows.map((_, i) => DONUT_PALETTE[i % DONUT_PALETTE.length]),
                borderColor: '#ffffff',
                borderWidth: 2,
            },
        ],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: { usePointStyle: true, pointStyle: 'rect', boxWidth: 10, padding: 10, font: { size: 11 } },
            },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => ` ${ctx.label}: ${formatNum(ctx.parsed)}`,
                },
            },
        },
    };

    const groupPath = (code: string) =>
        buildAlumniDirectoryPath(
            filterKind === 'functionalArea'
                ? { functionalAreaCode: code, instituteId: campusId }
                : { industryCode: code, instituteId: campusId }
        );

    const companyPath = (code: string, companyId: string) =>
        buildAlumniDirectoryPath(
            filterKind === 'functionalArea'
                ? { functionalAreaCode: code, jobCompanyId: companyId, instituteId: campusId }
                : { industryCode: code, jobCompanyId: companyId, instituteId: campusId }
        );

    return (
        <SectionCard title={title} subtitle={`${rows.length} ${unit}`} badge={`${formatNum(data.total.count)} alumni`}>
            <div className="row g-4 align-items-center">
                <div className="col-12 col-lg-5">
                    <div style={{ height: 320 }}>
                        <Doughnut data={chartData} options={options} />
                    </div>
                </div>
                <div className="col-12 col-lg-7">
                    {/* header row */}
                    <div
                        className="d-flex align-items-center px-2 py-2 mb-1"
                        style={{ background: '#f7f9fb', borderRadius: 6 }}>
                        <div style={{ flex: 1 }} className="font-12 font-600 text-uppercase">
                            <span style={{ color: '#8a97a5' }}>{nameHeader}</span>
                        </div>
                        <div style={{ width: 72 }} className="text-end font-12 font-600">
                            <span style={{ color: '#8a97a5' }}>#</span>
                        </div>
                        <div style={{ width: 64 }} className="text-end font-12 font-600">
                            <span style={{ color: '#8a97a5' }}>%</span>
                        </div>
                    </div>
                    {/* data rows */}
                    {rows.map((r, i) => {
                        const rowCode = enableDirectoryLinks && filterKind ? resolveRowCode(r, domain, filterKind) : null;
                        const nameHref = rowCode ? groupPath(rowCode) : null;
                        return (
                            <div key={`${r.name}-${i}`} className="d-flex align-items-start px-2 py-2 border-bottom">
                                <div style={{ flex: 1 }}>
                                    {nameHref ? (
                                        <a
                                            href={nameHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={breakupNameLink}
                                            title="View in Alumni Directory">
                                            <span className="font-15 font-600">{r.name}</span>
                                        </a>
                                    ) : (
                                        <div className="font-15 font-600">{r.name}</div>
                                    )}
                                    {r.topCompanies?.length > 0 && (
                                        <div className="mt-1" style={{ position: 'relative', display: 'inline-block' }}>
                                            <div
                                                className="d-flex flex-wrap"
                                                style={{ gap: 4 }}
                                                onMouseEnter={() => setHoveredRow(i)}
                                                onMouseLeave={() => setHoveredRow(null)}>
                                                {r.topCompanies.map((c, ci) => {
                                                    const label = companyDisplayName(c);
                                                    const companyId = rowCode ? resolveCompanyId(c, domain) : null;
                                                    const href = companyId ? companyPath(rowCode as string, companyId) : null;
                                                    return href ? (
                                                        <a
                                                            key={`${label}-${ci}`}
                                                            href={href}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={companyChipLink}
                                                            title="View in Alumni Directory"
                                                            onClick={(e) => e.stopPropagation()}>
                                                            {label}
                                                        </a>
                                                    ) : (
                                                        <span key={`${label}-${ci}`} style={companyChip}>
                                                            {label}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                            {hoveredRow === i && <div style={companiesTooltip}>{companiesLabel}</div>}
                                        </div>
                                    )}
                                </div>
                                <div style={{ width: 72 }} className="text-end font-15 font-600">
                                    {formatNum(r.count)}
                                </div>
                                <div style={{ width: 64 }} className="text-end">
                                    <span style={pctPill}>{formatPct(r.pct, 0)}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </SectionCard>
    );
};

export default BreakupSection;
